/**
 * ShareButton — mint a SpLink short URL + QR code for the current page.
 *
 * Behaviour:
 *   1. On first click, POST /api/splink/shorten with the current URL
 *   2. Show a modal with the short URL, QR code, and Copy / native-share
 *   3. Cache the response in-memory so re-opening is free
 *
 * The button falls back to plain "Copy link" when the user is signed out
 * (SpLink shortens are auth-scoped so we don't leak tenant keys or spam
 * anonymous short URLs).
 */
import { useState } from "react";
import { Share2, Copy, X, Check, Loader2, ExternalLink } from "lucide-react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { resolveSiteSlug } from "@/config/siteConfig";

// One in-memory cache per browser session — a full page reload starts fresh
const CACHE = new Map();

export default function ShareButton({
  url,           // optional — defaults to window.location.href
  label,         // optional — SpLink saves this as the link's human name
  title,         // page title shown in native share sheet
  compact,       // boolean — smaller circular button variant
  className,
  testId = "share-button",
}) {
  const { user } = useAuth();
  const [open,    setOpen]    = useState(false);
  const [loading, setLoading] = useState(false);
  const [data,    setData]    = useState(null);   // { short_url, qr_code_url, id }
  const [copied,  setCopied]  = useState(false);

  const targetUrl = url || (typeof window !== "undefined" ? window.location.href : "");

  async function fetchShort() {
    if (data) return data;
    const cached = CACHE.get(targetUrl);
    if (cached) { setData(cached); return cached; }
    if (!user) return null;
    setLoading(true);
    try {
      const site = resolveSiteSlug();
      const res = await http.post(`/splink/shorten`, { long_url: targetUrl, label, site });
      CACHE.set(targetUrl, res.data);
      setData(res.data);
      return res.data;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't generate a short link.";
      toast.error(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }

  async function openModal() {
    setOpen(true);
    if (user) await fetchShort();
  }

  async function nativeShare() {
    const short = data?.short_url || targetUrl;
    if (navigator.share) {
      try {
        await navigator.share({ title: title || document.title, url: short });
      } catch {/* user cancelled */}
    } else {
      copyLink(short);
    }
  }

  function copyLink(link) {
    const value = link || data?.short_url || targetUrl;
    try {
      navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy link");
    }
  }

  const btnBase = "inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border font-semibold hover:shadow-sm transition-shadow";
  return (
    <>
      <button
        type="button"
        onClick={openModal}
        title="Share this page"
        className={compact
          ? "inline-flex items-center justify-center w-9 h-9 rounded-full border bg-white hover:shadow-sm transition-shadow"
          : `${btnBase} bg-white`}
        style={{ borderColor: "#C79A3F", color: "#8B5E10" }}
        data-testid={testId}
      >
        <Share2 className="w-3.5 h-3.5" />
        {!compact && <span>Share</span>}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          style={{ background: "rgba(26,22,19,0.55)" }}
          onClick={() => setOpen(false)}
          data-testid="share-modal-backdrop"
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white overflow-hidden shadow-xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="share-modal"
          >
            <div className="flex items-center justify-between px-5 py-3 border-b"
                 style={{ borderColor: "#E9D8B4" }}>
              <div className="font-display text-lg" style={{ color: "#1A1613" }}>
                Share this page
              </div>
              <button onClick={() => setOpen(false)}
                      className="p-1 rounded hover:bg-black/5"
                      data-testid="share-modal-close">
                <X className="w-4 h-4" />
              </button>
            </div>

            {!user && (
              <div className="p-5 space-y-3">
                <p className="text-sm opacity-80" style={{ color: "#3E2A0A" }}>
                  Copy the direct link, or sign in to mint a short branded URL with QR code.
                </p>
                <div className="flex items-center gap-2 border rounded-lg p-2"
                     style={{ borderColor: "#E9D8B4" }}>
                  <input readOnly value={targetUrl}
                         className="flex-1 text-xs px-2 py-1 bg-transparent outline-none"
                         data-testid="share-long-url" />
                  <button onClick={() => copyLink(targetUrl)}
                          className="text-xs px-2 py-1 rounded font-semibold"
                          style={{ background: "#8B5E10", color: "#fff" }}
                          data-testid="share-copy-long">
                    {copied ? <Check className="w-3 h-3 inline" /> : "Copy"}
                  </button>
                </div>
                <button onClick={nativeShare}
                        className={`w-full ${btnBase} justify-center`}
                        style={{ background: "#F0E2C7", borderColor: "#C79A3F", color: "#8B5E10" }}
                        data-testid="share-native">
                  <Share2 className="w-3.5 h-3.5" /> Share via…
                </button>
              </div>
            )}

            {user && loading && (
              <div className="p-8 flex flex-col items-center gap-3">
                <Loader2 className="w-6 h-6 animate-spin" style={{ color: "#8B5E10" }} />
                <div className="text-xs opacity-70">Minting your short link…</div>
              </div>
            )}

            {user && !loading && data && (
              <div className="p-5 space-y-4">
                {data.qr_code_url && (
                  <div className="flex justify-center">
                    <img src={data.qr_code_url} alt="QR code"
                         className="w-40 h-40 border rounded-lg"
                         style={{ borderColor: "#E9D8B4" }}
                         data-testid="share-qr" />
                  </div>
                )}
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest mb-1"
                       style={{ color: "#B25D38" }}>Short link</div>
                  <div className="flex items-center gap-2 border rounded-lg p-2"
                       style={{ borderColor: "#E9D8B4" }}>
                    <input readOnly value={data.short_url}
                           className="flex-1 text-sm px-2 py-1 bg-transparent outline-none font-mono"
                           data-testid="share-short-url" />
                    <a href={data.short_url} target="_blank" rel="noopener noreferrer"
                       className="p-1.5 rounded hover:bg-black/5"
                       title="Open">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                    <button onClick={() => copyLink(data.short_url)}
                            className="text-xs px-2 py-1.5 rounded font-semibold"
                            style={{ background: "#8B5E10", color: "#fff" }}
                            data-testid="share-copy-short">
                      {copied
                        ? <Check className="w-3.5 h-3.5 inline" />
                        : <><Copy className="w-3.5 h-3.5 inline mr-1" />Copy</>}
                    </button>
                  </div>
                </div>
                <button onClick={nativeShare}
                        className={`w-full ${btnBase} justify-center`}
                        style={{ background: "#F0E2C7", borderColor: "#C79A3F", color: "#8B5E10" }}
                        data-testid="share-native">
                  <Share2 className="w-3.5 h-3.5" /> Share via…
                </button>
                <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 text-center">
                  Powered by SpLink
                </div>
              </div>
            )}

            {user && !loading && !data && (
              <div className="p-5 space-y-3">
                <p className="text-sm opacity-80">
                  Short-link service is unavailable right now — you can still copy the direct URL.
                </p>
                <div className="flex items-center gap-2 border rounded-lg p-2"
                     style={{ borderColor: "#E9D8B4" }}>
                  <input readOnly value={targetUrl}
                         className="flex-1 text-xs px-2 py-1 bg-transparent outline-none" />
                  <button onClick={() => copyLink(targetUrl)}
                          className="text-xs px-2 py-1 rounded font-semibold"
                          style={{ background: "#8B5E10", color: "#fff" }}>
                    {copied ? <Check className="w-3 h-3 inline" /> : "Copy"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
